import React, { useEffect, useRef, useState } from 'react';
import { Zap, Shield, Users, ChevronRight, BarChart3, FileText, Globe, CheckCircle } from 'lucide-react';
import { TrustBadge } from '../components/TrustBadge';
import { LEGAL_ENTITY } from '../config/legalEntity';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_658a2c5a-a06c-4211-b54e-d929806b87d8/artifacts/7wlqr7yi_ApplyDrive%20Background%20Removed.png';

const PURPLE = {
  deep: '#2D1160',
  primary: '#3B1578',
  hover: '#4E1F9E',
  mid: '#6B3FA0',
  light: '#EDE5FF',
  faint: '#F8F5FF',
  ring: '#D4C4F0',
};

// Intersection Observer hook for scroll animations
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

const ApplyDriveLanding = () => {
  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="ApplyDrive.uk" className="h-20 w-auto" data-testid="header-logo" />
          </div>
          <a
            href="mailto:contact@applydrive.uk"
            className="text-sm font-medium transition-colors"
            style={{ color: PURPLE.mid }}
            data-testid="contact-link"
          >
            Get in touch
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-16 sm:pt-24 pb-8 sm:pb-12" style={{ background: `linear-gradient(180deg, ${PURPLE.faint} 0%, #FFFFFF 100%)` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <FadeIn>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight" style={{ color: PURPLE.deep }} data-testid="hero-heading">
                Vehicle finance applications, simplified.
              </h1>
            </FadeIn>
            <FadeIn delay={0.15}>
              <p className="mt-6 text-lg sm:text-xl text-slate-500 leading-relaxed max-w-2xl mx-auto">
                The platform that powers vehicle finance applications for dealers, brokers and motor groups. OCR extraction, branded portals, and direct API submission — all in one place.
              </p>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="mailto:contact@applydrive.uk"
                  className="inline-flex items-center gap-2 px-8 py-3.5 text-white rounded-full text-sm font-semibold transition-all shadow-lg hover:shadow-xl hover:scale-105"
                  style={{ backgroundColor: PURPLE.primary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = PURPLE.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = PURPLE.primary}
                  data-testid="hero-cta"
                >
                  Request a Demo
                  <ChevronRight className="w-4 h-4" />
                </a>
                <p className="text-sm text-slate-400">No commitment. See it in action first.</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.45}>
              <TrustBadge className="mt-6" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 sm:py-28 border-t" style={{ borderColor: PURPLE.ring }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-base sm:text-lg font-semibold" style={{ color: PURPLE.deep }}>Everything you need</h2>
              <p className="mt-2 text-sm text-slate-500">From proposal to submission in seconds, not minutes.</p>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Data Extraction', desc: 'Upload a PDF, snap a photo, or paste text. AI extracts every detail from proposals instantly.' },
              { icon: Users, title: 'Your Brand, Your Portal', desc: 'Every partner gets their own branded application form — unique URL, logo, and colours. Fully white-labelled.' },
              { icon: Globe, title: 'Direct API Submission', desc: "Applications go straight to your Proposal Gateway via each source's own API key. No manual re-keying." },
              { icon: Shield, title: 'GDPR Compliant', desc: 'Ephemeral processing — no personal data stored at rest. Full data transparency and audit trail.' },
              { icon: BarChart3, title: 'Analytics', desc: 'Application volumes, conversion rates, and submission success — broken down by source.' },
              { icon: FileText, title: 'PDF Export', desc: 'Generate professional application summaries for your records before submission.' },
            ].map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.08}>
                <FeatureCard icon={f.icon} title={f.title} desc={f.desc} />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Showcase */}
      <section className="py-20 sm:py-28 border-t" style={{ backgroundColor: PURPLE.faint, borderColor: PURPLE.ring }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[5fr_7fr] gap-12 items-start">
            <FadeIn>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: PURPLE.deep }}>
                  An application form built to convert.
                </h2>
                <p className="mt-4 text-slate-500 leading-relaxed">
                  Your customers are guided through a clean, step-by-step journey. No confusion, no drop-offs. Just a seamless experience that gets applications submitted.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'Instant DVLA vehicle lookup from registration',
                    'UK address search with postcode autocomplete',
                    'Smart validation catches errors before submission',
                    'Works flawlessly on mobile, tablet, and desktop',
                    'Branded to your business — customers never see ApplyDrive'
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: PURPLE.light }}>
                        <CheckCircle className="w-3 h-3" style={{ color: PURPLE.primary }} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl opacity-15 blur-3xl" style={{ background: PURPLE.mid }} />
                <div className="relative rounded-xl overflow-hidden shadow-2xl border" style={{ borderColor: PURPLE.ring }}>
                  <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-white rounded-md px-3 py-1 text-xs text-slate-400 border border-slate-200 max-w-md mx-auto text-center">
                        applydrive.uk/s/your-brand
                      </div>
                    </div>
                  </div>
                  <video
                    src="/form-demo.mp4"
                    poster="/product-abc-form.jpg"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full"
                    data-testid="form-mockup"
                  />
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-28 border-t" style={{ borderColor: PURPLE.ring }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-base sm:text-lg font-semibold" style={{ color: PURPLE.deep }}>How it works</h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { num: '1', title: 'We set you up', desc: 'Your brand, logo, colours, and API key — configured in minutes.' },
              { num: '2', title: 'Share your link', desc: 'Give your unique URL to staff. They paste proposals or customers apply directly.' },
              { num: '3', title: 'Applications flow', desc: 'Data is extracted, validated, and submitted to your Proposal Gateway automatically.' },
            ].map((s, i) => (
              <FadeIn key={s.num} delay={i * 0.12}>
                <StepCard num={s.num} title={s.title} desc={s.desc} />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 border-t" style={{ borderColor: PURPLE.ring }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: PURPLE.deep }}>Ready to streamline your finance applications?</h2>
            <p className="mt-4 text-sm text-slate-500">Join the dealers and brokers already saving hours per <a href="/admin/applydrive-admin" className="text-slate-500 no-underline cursor-text" data-testid="admin-shortcut">week</a>.</p>
            <div className="mt-8">
              <a
                href="mailto:contact@applydrive.uk"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-white rounded-full text-sm font-semibold transition-all shadow-lg hover:shadow-xl hover:scale-105"
                style={{ backgroundColor: PURPLE.primary }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = PURPLE.hover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = PURPLE.primary}
                data-testid="bottom-cta"
              >
                Get in touch
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8" style={{ borderColor: PURPLE.ring }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <img src={LOGO_URL} alt="ApplyDrive.uk" className="h-10 w-auto" />
          </div>
          <p className="text-xs text-slate-400">
            Vehicle finance applications, simplified.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-4 text-xs">
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700 hover:underline" data-testid="footer-privacy-link">Privacy Policy</a>
            <a href="/platform-notice" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700 hover:underline" data-testid="footer-platform-notice-link">Platform Notice</a>
            <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`} className="text-slate-500 hover:text-slate-700 hover:underline" data-testid="footer-privacy-email">{LEGAL_ENTITY.privacyEmail}</a>
          </div>
          <p className="text-[11px] text-slate-400 mt-4 leading-relaxed max-w-2xl mx-auto" data-testid="footer-legal-entity">
            {LEGAL_ENTITY.tradingName} is a trading name of <strong className="text-slate-500">{LEGAL_ENTITY.companyName}</strong>, a company registered in {LEGAL_ENTITY.jurisdiction} (Company No.{' '}
            <a href={LEGAL_ENTITY.companiesHouseUrl} target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 underline-offset-2 hover:underline">{LEGAL_ENTITY.companyNumber}</a>
            ). Registered office: {LEGAL_ENTITY.registeredOffice}. ICO Registration:{' '}
            <a href={LEGAL_ENTITY.icoRegisterUrl} target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 underline-offset-2 hover:underline">{LEGAL_ENTITY.icoNumber}</a>.
          </p>
          <p className="text-xs text-slate-400 mt-3">
            &copy; {new Date().getFullYear()} {LEGAL_ENTITY.companyName}. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Customer notice */}
      <div className="py-4" style={{ backgroundColor: PURPLE.deep }}>
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-xs" style={{ color: PURPLE.ring }}>
            Looking to apply for vehicle finance? Please use the link provided to you by your dealer or broker.
          </p>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div
    className="p-5 rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-default text-center"
    style={{ borderColor: PURPLE.ring }}
    onMouseEnter={(e) => e.currentTarget.style.borderColor = PURPLE.mid}
    onMouseLeave={(e) => e.currentTarget.style.borderColor = PURPLE.ring}
    data-testid={`feature-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 mx-auto" style={{ backgroundColor: PURPLE.light }}>
      <Icon className="w-4.5 h-4.5" style={{ color: PURPLE.mid }} />
    </div>
    <h3 className="text-sm font-semibold mb-1" style={{ color: PURPLE.deep }}>{title}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
  </div>
);

const StepCard = ({ num, title, desc }) => (
  <div className="text-center" data-testid={`step-${num}`}>
    <div
      className="w-10 h-10 rounded-full text-white flex items-center justify-center text-sm font-bold mx-auto mb-3 transition-transform duration-300 hover:scale-110"
      style={{ backgroundColor: PURPLE.primary }}
    >
      {num}
    </div>
    <h3 className="text-sm font-semibold mb-1" style={{ color: PURPLE.deep }}>{title}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
  </div>
);

export default ApplyDriveLanding;
